// /* eslint-disable import/no-extraneous-dependencies, no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
// eslint-disable-next-line import/no-unresolved
import EventCard from "@qlik/qlik/src/components/CardDeckContainer/Event_Cards/ContentEventCard";
import Pagination from "@qlik/qlik/src/components/Common/Pagination";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import { EventConstants } from "@qlik/qlik/src/lib/constants/event-constants";
import { scrollToElement } from "@qlik/qlik/src/lib/util/helpers";
import EventData from "@qlik/qlik/src/graphql/queries/events";
import { getMicrocopyValueFromKeys, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * Displays a list of past events with pagination support.
 *
 * @component
 * @param {Object} data - The data object containing configuration for the component.
 * @param {Object} data.cardItem - The configuration for the card item displayed alongside the events.
 * @returns {JSX.Element} The PastEventListing component.
 */
const PastEventListing = ({ data }) => {
    const [events, setEvents] = useState([]);
    const [filters, setFilters] = useState({
        page: 0,
        limit: EventConstants.EVENT_NUMBER_PER_PAGE,
    });

    const { i18n } = useI18n();
    const eventData = EventData(i18n.curr.enLangName);

    // Paginates an array of items based on the specified page size and current page.
    const paginate = (items, pageSize, currentPage) => {
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageData = items.slice(startIndex, endIndex);
        return currentPageData;
    };

    const [total, setTotal] = useState(eventData.pastEvents.nodes.length);

    const sectionRef = useRef(null);
    const handlePageChange = (selectedPage) => {
        setFilters((prev) => ({ ...prev, page: selectedPage }));
        scrollToElement(sectionRef);
    };

    const [noResultsText] = getMicrocopyValueFromKeys(data?.microcopyGroup, [EventConstants.EVENT_NO_RECORDS]);

    useEffect(() => {
        const pastEvents = eventData.pastEvents.nodes
            // If there is an endDate, then filter on that, otherwise filter on the startDate
            .filter(
                (event) =>
                    (event?.languages?.includes("All") || event?.languages?.includes(i18n.curr.enLangName)) &&
                    (event.endDate
                        ? new Date(event.endDate).getTime() < Date.now()
                        : new Date(event.startDate).getTime() < Date.now())
            )
            .sort((a, b) => {
                // First, prioritize sorting by null startDate values
                if (a.startDate === null && b.startDate !== null) return 1;
                if (a.startDate !== null && b.startDate === null) return -1;

                // Then, if both startDates are non-null or both are null, sort by startDate
                return new Date(b.startDate) - new Date(a.startDate);
            });
        setTotal(pastEvents.length);
        const paginatedData = paginate(pastEvents, filters.limit, filters.page);
        setEvents(paginatedData);
    }, [eventData.pastEvents.nodes, filters]);

    return (
        <section ref={sectionRef} className="pt-15 lg:pt-30 pb-10 lg:pb-20" id={data?.customId}>
            <div className="container">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-7.5 gap-y-7.5 lg:gap-y-15">
                    <div className="h-full min-h-75 p-10 bg-blue bg-eventCardBg bg-no-repeat bg-cover md:bg-contain bg-top md:bg-bottom rounded">
                        {data.cardItem?.title && (
                            <h2
                                className="text-white"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(data.cardItem?.title) }}
                            />
                        )}
                    </div>
                    {!events.length && (
                        <div className="px-5 lg:px-10 col-span-2">
                            <NoResults text={noResultsText} />
                        </div>
                    )}
                    {events?.map((singleEvent) => (
                        <EventCard key={singleEvent.id} card={singleEvent} />
                    ))}
                </div>
                <div className="row">
                    <Pagination
                        currentPage={filters.page}
                        limit={filters.limit}
                        total={total}
                        handlePageChange={handlePageChange}
                    />
                </div>
            </div>
        </section>
    );
};
export default PastEventListing;
